import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import type { ApiTokenInfo, SessionInfo } from "../api/types";
import { type MessageKey, setLang, useLang, useT } from "../i18n";
import { useApp, useSyncState } from "../store";
import { updateUser } from "../sync/actions";
import { IconClose } from "../ui/icons";
import { Confirm, Modal } from "../ui/primitives";
import { applyTheme, currentThemeSetting, type ThemeSetting } from "./theme";

const TABS: { key: string; label: MessageKey }[] = [
  { key: "account", label: "account" },
  { key: "general", label: "general" },
  { key: "theme", label: "theme" },
  { key: "integrations", label: "integrations" },
];

function timezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (fn) return fn("timeZone");
  } catch {
    // ignore
  }
  return ["UTC", "Asia/Shanghai", "Europe/London", "America/New_York"];
}

export function Settings() {
  const tab = useApp((s) => s.settingsOpen);
  const set = useApp((s) => s.set);
  const t = useT();
  if (!tab) return null;
  const close = () => set({ settingsOpen: null });
  return (
    <Modal onClose={close} className="settings" label={t("settings")}>
      <div className="settings-nav">
        <div className="menu-title" style={{ padding: "6px 8px 10px", fontSize: 16, color: "var(--text)" }}>
          {t("settings")}
        </div>
        {TABS.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`nav-item${tab === x.key ? " active" : ""}`}
            onClick={() => set({ settingsOpen: x.key })}
            data-testid={`settings-tab-${x.key}`}
          >
            <span className="label">{t(x.label)}</span>
          </button>
        ))}
      </div>
      <div className="settings-body">
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 8 }}>
          <button type="button" className="icon-btn" onClick={close} aria-label={t("close")}>
            <IconClose size={20} />
          </button>
        </div>
        {tab === "account" && <AccountTab />}
        {tab === "general" && <GeneralTab />}
        {tab === "theme" && <ThemeTab />}
        {tab === "integrations" && <IntegrationsTab />}
      </div>
    </Modal>
  );
}

function AccountTab() {
  const user = useSyncState().user;
  const t = useT();
  const [name, setName] = useState(user?.full_name ?? "");
  const tzs = timezones();
  return (
    <div>
      <h2>{t("account")}</h2>
      <label className="field">
        <span>{t("fullName")}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
          <button
            type="button"
            className="btn btn-primary"
            disabled={!name.trim() || name === user?.full_name}
            onClick={() => void updateUser({ full_name: name.trim() })}
          >
            {t("save")}
          </button>
        </div>
      </label>
      <label className="field">
        <span>{t("email")}</span>
        <input className="input" value={user?.email ?? ""} readOnly />
      </label>
      <label className="field">
        <span>{t("timezone")}</span>
        <select
          className="input"
          value={user?.tz_info.timezone ?? "UTC"}
          onChange={(e) => void updateUser({ timezone: e.target.value })}
          data-testid="settings-timezone"
        >
          {!tzs.includes(user?.tz_info.timezone ?? "UTC") && <option>{user?.tz_info.timezone}</option>}
          {tzs.map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function GeneralTab() {
  const user = useSyncState().user;
  const lang = useLang();
  const t = useT();
  const wd =
    lang === "zh"
      ? ["周一", "周二", "周三", "周四", "周五", "周六", "周日"]
      : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  return (
    <div>
      <h2>{t("general")}</h2>
      <label className="field">
        <span>{t("language")}</span>
        <select
          className="input"
          value={lang}
          data-testid="settings-language"
          onChange={(e) => {
            const v = e.target.value as "en" | "zh";
            setLang(v);
            void updateUser({ lang: v === "zh" ? "zh_CN" : "en" });
          }}
        >
          <option value="zh">中文（简体）</option>
          <option value="en">English</option>
        </select>
      </label>
      <label className="field">
        <span>{t("startDay")}</span>
        <select
          className="input"
          value={user?.start_day ?? 1}
          onChange={(e) => void updateUser({ start_day: Number(e.target.value) })}
        >
          {wd.map((d, i) => (
            <option key={d} value={i + 1}>
              {d}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>{t("dateFormat")}</span>
        <select
          className="input"
          value={user?.date_format ?? 0}
          onChange={(e) => void updateUser({ date_format: Number(e.target.value) })}
        >
          <option value={0}>DD-MM-YYYY</option>
          <option value={1}>MM-DD-YYYY</option>
        </select>
      </label>
      <label className="field">
        <span>{t("timeFormat")}</span>
        <select
          className="input"
          value={user?.time_format ?? 0}
          onChange={(e) => void updateUser({ time_format: Number(e.target.value) })}
        >
          <option value={0}>24h (13:00)</option>
          <option value={1}>12h (1:00 PM)</option>
        </select>
      </label>
    </div>
  );
}

function ThemeTab() {
  const t = useT();
  const [theme, setTheme] = useState<ThemeSetting>(currentThemeSetting());
  const cards: { key: ThemeSetting; label: MessageKey; colors: [string, string, string] }[] = [
    { key: "light", label: "light", colors: ["#fcfaf8", "#ffffff", "#dc4c3e"] },
    { key: "dark", label: "dark", colors: ["#282828", "#1e1e1e", "#de4c4a"] },
    { key: "system", label: "system", colors: ["#fcfaf8", "#1e1e1e", "#dc4c3e"] },
  ];
  return (
    <div>
      <h2>{t("theme")}</h2>
      <div className="theme-cards">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`theme-card${theme === c.key ? " selected" : ""}`}
            data-testid={`theme-${c.key}`}
            onClick={() => {
              applyTheme(c.key);
              setTheme(c.key);
            }}
          >
            <div className="theme-swatch">
              <div style={{ width: "35%", background: c.colors[0] }} />
              <div style={{ flex: 1, background: c.colors[1], padding: 8 }}>
                <div style={{ height: 6, width: "60%", background: c.colors[2], borderRadius: 3 }} />
              </div>
            </div>
            {t(c.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const t = useT();
  const client = useApp((s) => s.client);
  const [tokens, setTokens] = useState<ApiTokenInfo[] | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [revoke, setRevoke] = useState<null | { label: string; run: () => Promise<void> }>(null);

  const load = useCallback(async () => {
    try {
      const r = await client.get<{ results: ApiTokenInfo[] }>("/letsdoit/tokens");
      setTokens(r.results);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    try {
      const r = await client.get<{ results: SessionInfo[] } | SessionInfo[]>("/letsdoit/sessions");
      setSessions(Array.isArray(r) ? r : r.results);
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 404)) setError(e instanceof Error ? e.message : String(e));
      setSessions(null);
    }
  }, [client]);
  useEffect(() => {
    void load();
  }, [load]);

  const fmt = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : t("never"));
  return (
    <div>
      <h2>{t("apiTokens")}</h2>
      {error && <div className="error-text">{error}</div>}
      <form
        style={{ display: "flex", gap: 8, marginBottom: 12 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          try {
            const r = await client.post<ApiTokenInfo>("/letsdoit/tokens", { name: name.trim() });
            setCreated(r.token ?? null);
            setName("");
            await load();
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          }
        }}
      >
        <input
          className="input"
          placeholder={t("tokenName")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="token-name"
        />
        <button type="submit" className="btn btn-primary" disabled={!name.trim()} data-testid="token-create">
          {t("createToken")}
        </button>
      </form>
      {created && (
        <div>
          <div className="small muted">{t("newTokenOnce")}</div>
          <div className="secret" data-testid="token-secret">
            {created}
          </div>
        </div>
      )}
      <table className="table" data-testid="token-table">
        <tbody>
          {tokens?.map((tk) => (
            <tr key={tk.id}>
              <td>
                <strong>{tk.name}</strong> <span className="muted small">({tk.kind})</span>
                {tk.is_current && (
                  <span className="small" style={{ color: "var(--today)" }}>
                    {" "}
                    · {t("current")}
                  </span>
                )}
                <div className="muted small">
                  …{tk.fingerprint} · {t("lastUsed")}: {fmt(tk.last_used_at)}
                </div>
              </td>
              <td style={{ textAlign: "right" }}>
                {tk.kind !== "personal" && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      setRevoke({
                        label: tk.name,
                        run: async () => {
                          await client.delete(`/letsdoit/tokens/${tk.id}`);
                          await load();
                        },
                      })
                    }
                  >
                    {t("revoke")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sessions && (
        <>
          <h2>{t("sessions")}</h2>
          <table className="table" data-testid="session-table">
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.name ?? s.user_agent ?? s.id}</strong>
                    {s.is_current && (
                      <span className="small" style={{ color: "var(--today)" }}>
                        {" "}
                        · {t("current")}
                      </span>
                    )}
                    <div className="muted small">
                      {fmt(s.created_at)} · {t("lastUsed")}: {fmt(s.last_used_at)}
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        setRevoke({
                          label: s.name ?? s.id,
                          run: async () => {
                            await client.delete(`/letsdoit/sessions/${s.id}`);
                            if (s.is_current) await useApp.getState().logout();
                            else await load();
                          },
                        })
                      }
                    >
                      {t("revoke")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {revoke && (
        <Confirm
          message={`${t("revoke")} “${revoke.label}”?`}
          confirmLabel={t("revoke")}
          cancelLabel={t("cancel")}
          onConfirm={() => void revoke.run().catch((e: Error) => setError(e.message))}
          onClose={() => setRevoke(null)}
        />
      )}
    </div>
  );
}
